import React, { useEffect, useState, useRef, useCallback } from 'react';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import { useToast } from 'context/providers/ToastProvider';
import { Card, Col, Row, Spinner, Badge } from 'react-bootstrap';
import 'react-loading-skeleton/dist/skeleton.css';

const SeccionLogs = ({ idLicencia }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();

    const [logServicios, setLogServicios] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const observerTarget = useRef(null);
    const allLogsRef = useRef(null);
    const pageSize = 10;

    /**
     * Obtiene los datos Seccion Logs con paginación
     */
    const ObtieneSeccionLogs = useCallback(async (page = 1, append = false) => {
        try {
            if (append) {
                setIsLoadingMore(true);
            }

            const response = await fetch(`/api/licencia/ObtieneSeccionLogs/?id=${idLicencia}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = "";
                    return;
                }
                throw new Error(`${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            if (data.iResponseType == 1) {
                const ObjDetalles = JSON.parse(data.detalles);

                const allLogs = ObjDetalles.table2 || [];

                if (!allLogsRef.current) {
                    allLogsRef.current = allLogs;
                }

                // Simular paginación del lado del cliente
                const startIndex = (page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const paginatedLogs = allLogs.slice(startIndex, endIndex);

                if (append) {
                    setLogServicios(prev => [...prev, ...paginatedLogs]);
                } else {
                    setLogServicios(paginatedLogs);
                }

                // Si no hay más datos para mostrar
                setHasMore(endIndex < allLogs.length);
            }

        } catch (error) {
            showErrorToast(error.message);
            console.error('Error al obtener datos de la licencia:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [idLicencia, token, showErrorToast]); 

    /**
     * Carga más logs al hacer scroll
     */
    const loadMoreLogs = useCallback(() => {
        if (!isLoadingMore && hasMore) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);

            if (allLogsRef.current) {
                const startIndex = (nextPage - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const paginatedLogs = allLogsRef.current.slice(startIndex, endIndex);
                setLogServicios(prev => [...prev, ...paginatedLogs]);
                setHasMore(endIndex < allLogsRef.current.length);
            } else {
                ObtieneSeccionLogs(nextPage, true);
            }
        }
    }, [pageNumber, isLoadingMore, hasMore, ObtieneSeccionLogs]);

    /**
     * Intersection Observer para detectar scroll al final
     */
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
                    loadMoreLogs();
                }
            },
            { threshold: 1.0 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [loadMoreLogs, hasMore, isLoadingMore]);

    useEffect(() => {
        // Reset cuando cambia la licencia
        setLogServicios([]);
        setPageNumber(1);
        setHasMore(true);
        allLogsRef.current = null; 
        ObtieneSeccionLogs(1, false);
    }, [idLicencia, ObtieneSeccionLogs]);


    return (
        <Row className="g-3 mb-3">
            <Col lg={12}>
                <Card className="mb-3">
                    <Card.Header>
                        <h5 className="mb-0">&Uacute;ltimos Logs del licenciatario</h5>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div style={{ maxHeight: '420px', overflowY: 'auto', overflowX: 'hidden', paddingLeft: '1rem', boxSizing: 'border-box' }}>
                            <div className="sticky-top bg-white" style={{ zIndex: 2, borderBottom: '1px solid #e9ecef', paddingBottom: '0.5rem', marginBottom: '0.5rem' }}>
                                <Row className="align-items-center gx-2">
                                    <Col md={2} className="text-muted small fw-bold">Fecha movimiento</Col>
                                    <Col md={2} className="text-muted small fw-bold">Usuario</Col>
                                    <Col md={2} className="text-muted small fw-bold">Nombre Sucursal</Col>
                                    <Col md={1} className="text-muted small fw-bold">Versión</Col>
                                    <Col md={4} className="text-muted small fw-bold">Movimiento</Col>
                                </Row>
                            </div>

                            {logServicios.length === 0 && !isLoadingMore ? (
                                <div className="text-center text-muted py-5">
                                    <p>No hay logs disponibles</p>
                                </div>
                            ) : (
                                <Row className="g-3">
                                    {logServicios.map((log, index) => (
                                            <Col key={index} xs={12}>
                                                <Card className={`shadow-sm ${log.bIsPeriodoPrueba ? 'border-danger' : ''}`}>
                                                    <Card.Body className="p-2">
                                                        <Row className="align-items-start">
                                                            <Col md={2}>
                                                                <div className="text-muted small">
                                                                    {(() => {
                                                                        try {
                                                                            const d = new Date(log.dCreatedDate);
                                                                            const dateStr = d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
                                                                            const timeStr = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                                                                            return `${dateStr} ${timeStr}`;
                                                                        } catch (e) {
                                                                            return log.dCreatedDate;
                                                                        }
                                                                    })()}
                                                                </div>
                                                            </Col>
                                                            <Col md={2}>
                                                                <div className={`small ${log.bIsPeriodoPrueba ? 'text-danger' : ''}`} style={{ whiteSpace: 'normal' }}>
                                                                    {log.vCreatedUser}
                                                                </div>
                                                            </Col>
                                                            <Col md={2}>
                                                                <div className={`small ${log.bIsPeriodoPrueba ? 'text-danger' : ''}`} style={{ whiteSpace: 'normal', overflowWrap: 'anywhere'  }}>
                                                                    {log.vNombreSucursal}
                                                                </div>
                                                            </Col>
                                                            <Col md={1}>
                                                                <Badge bg="secondary" className="font-monospace small">{log.vVersion}</Badge>
                                                            </Col>
                                                            <Col md={4}>
                                                                <div className={`small ${log.bIsPeriodoPrueba ? 'text-danger' : ''}`} style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                                    {log.vMovimiento}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                </Row>
                            )}

                            {/* Infinite scroll trigger */}
                            <div ref={observerTarget} style={{ height: '20px', marginTop: '1rem' }}>
                                {isLoadingMore && (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" />
                                        <span className="ms-2">Cargando más logs...</span>
                                    </div>
                                )}
                                {!hasMore && logServicios.length > 0 && (
                                    <div className="text-center py-3 text-muted">
                                        <small>No hay más logs para mostrar</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    )
}

SeccionLogs.propTypes = {

    idLicencia: PropTypes.string

};

export default SeccionLogs;